const OUTPUT_WIDTH = 1200;
const OUTPUT_HEIGHT = 1500;
const EDITOR_WIDTH = 600;
const EDITOR_HEIGHT = 750;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

class SunlitPendantUpload extends HTMLElement {
  connectedCallback() {
    if (this.dataset.nativeUploadReady === 'true') return;
    this.dataset.nativeUploadReady = 'true';
    this.states = new Map();
    this.previewUrls = new Map();
    this.activePhoto = null;
    this.workingState = null;
    this.dragState = null;

    if (!this.placeInsideProductForm()) {
      this.dataset.nativeUploadReady = 'waiting';
      this.waitForProductForm();
      return;
    }
    this.initialize();
  }

  placeInsideProductForm() {
    const details = document.querySelector('.product-details');
    const productForm = details?.querySelector('product-form-component') || document.querySelector('product-form-component');
    const form = productForm?.querySelector('form[data-type="add-to-cart-form"]');
    const formButtons = form?.querySelector('.product-form-buttons');
    if (!(form instanceof HTMLFormElement) || !(formButtons instanceof HTMLElement)) return false;

    form.enctype = 'multipart/form-data';
    formButtons.insertAdjacentElement('beforebegin', this);
    this.productForm = form;
    return true;
  }

  waitForProductForm() {
    let attempts = 0;
    const observer = new MutationObserver(() => {
      attempts += 1;
      if (this.placeInsideProductForm()) {
        observer.disconnect();
        this.dataset.nativeUploadReady = 'true';
        this.initialize();
      } else if (attempts > 80) {
        observer.disconnect();
        this.showGlobalError('Photo upload could not be loaded. Please refresh the page.');
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 10000);
  }

  initialize() {
    if (this.dataset.nativeUploadInitialized === 'true') return;
    this.dataset.nativeUploadInitialized = 'true';

    this.details = this.querySelector('[data-upload-details]');
    this.editorPanel = this.querySelector('[data-editor-panel]');
    this.canvas = this.querySelector('[data-editor-canvas]');
    this.zoomInput = this.querySelector('[data-zoom]');
    this.editorTitle = this.querySelector('[data-editor-title]');

    this.querySelectorAll('[data-photo-input]').forEach((input) => {
      input.addEventListener('change', () => this.handleFileSelection(input));
      input.addEventListener('invalid', (event) => {
        event.preventDefault();
        this.validateRequiredPhotos();
      });
    });
    this.querySelectorAll('[data-edit-photo]').forEach((button) => {
      button.addEventListener('click', () => this.openEditor(Number(button.dataset.editPhoto)));
    });
    this.querySelectorAll('[data-remove-photo]').forEach((button) => {
      button.addEventListener('click', () => this.removePhoto(Number(button.dataset.removePhoto)));
    });
    this.querySelector('[data-cancel-edit]')?.addEventListener('click', () => this.closeEditor());
    this.querySelector('[data-complete-upload]')?.addEventListener('click', () => this.completeUpload());
    this.querySelector('[data-rotate-left]')?.addEventListener('click', () => this.rotate(-90));
    this.querySelector('[data-rotate-right]')?.addEventListener('click', () => this.rotate(90));
    this.querySelector('[data-reset-edit]')?.addEventListener('click', () => this.resetEdit());
    this.querySelector('[data-apply-edit]')?.addEventListener('click', () => this.applyEdit());
    this.zoomInput?.addEventListener('input', () => {
      if (!this.workingState) return;
      this.workingState.zoom = Number(this.zoomInput.value);
      this.renderEditor();
    });

    this.canvas?.addEventListener('pointerdown', (event) => this.startDrag(event));
    this.canvas?.addEventListener('pointermove', (event) => this.drag(event));
    this.canvas?.addEventListener('pointerup', (event) => this.stopDrag(event));
    this.canvas?.addEventListener('pointercancel', (event) => this.stopDrag(event));

    this.productForm?.addEventListener(
      'click',
      (event) => {
        const clicked = event.target instanceof Element ? event.target : null;
        const submitButton = clicked?.closest('button[type="submit"], button[name="add"], input[type="submit"]');
        if (!submitButton || submitButton.form !== this.productForm || this.validateRequiredPhotos()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    this.productForm?.addEventListener(
      'submit',
      (event) => {
        if (this.validateRequiredPhotos()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );
  }

  validateRequiredPhotos() {
    const inputs = [...this.querySelectorAll('[data-photo-input]')];
    const missing = inputs.filter((input) => !input.files?.length);
    if (!missing.length && !this.dataset.processing) {
      this.clearGlobalError();
      return true;
    }
    this.showGlobalError(
      this.dataset.processing
        ? 'Please wait while your edited photos are prepared.'
        : 'Please upload both photos before adding to cart.'
    );
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return false;
  }

  async handleFileSelection(input) {
    const photoNumber = Number(input.dataset.photoInput);
    const file = input.files?.[0];
    if (!file) return;

    this.clearGlobalError();
    if (!ACCEPTED_TYPES.has(file.type)) {
      this.rejectFile(input, this.dataset.fileTypeError || 'Please choose a JPG, PNG or WEBP photo.');
      return;
    }
    if (file.size > (Number(this.dataset.maxSize) || MAX_FILE_SIZE)) {
      this.rejectFile(input, this.dataset.fileSizeError || 'Please choose a photo smaller than 10 MB.');
      return;
    }

    input.setCustomValidity('Please wait while your photo is prepared.');
    this.setSlotStatus(photoNumber, 'Preparing photo…');
    this.dataset.processing = 'true';

    try {
      const image = await this.loadImage(file);
      const state = {
        image,
        originalName: file.name,
        rotation: 0,
        zoom: 1,
        offsetX: 0,
        offsetY: 0,
      };
      await this.commitPhoto(photoNumber, state);
      input.setCustomValidity('');
      this.setSlotStatus(photoNumber, '');
    } catch (error) {
      console.error('[sunlit-native-upload]', error);
      this.rejectFile(input, 'This photo could not be prepared. Please choose another image.');
    } finally {
      delete this.dataset.processing;
    }
  }

  rejectFile(input, message) {
    input.value = '';
    input.setCustomValidity(message);
    this.showGlobalError(message);
    input.reportValidity();
  }

  async loadImage(file) {
    if ('createImageBitmap' in window) return createImageBitmap(file, { imageOrientation: 'from-image' });

    const url = URL.createObjectURL(file);
    try {
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
      await image.decode();
      return image;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  async commitPhoto(photoNumber, state) {
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = OUTPUT_WIDTH;
    outputCanvas.height = OUTPUT_HEIGHT;
    this.drawState(outputCanvas, state);

    const blob = await new Promise((resolve, reject) => {
      outputCanvas.toBlob((result) => (result ? resolve(result) : reject(new Error('canvas.toBlob failed'))), 'image/jpeg', 0.9);
    });
    const outputFile = new File([blob], `pendant-photo-${photoNumber}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });

    let transfer;
    try {
      transfer = new DataTransfer();
      transfer.items.add(outputFile);
    } catch (error) {
      throw new Error(this.dataset.browserError || 'DataTransfer is unavailable', { cause: error });
    }

    const input = this.querySelector(`[data-photo-input="${photoNumber}"]`);
    input.files = transfer.files;
    input.setCustomValidity('');
    this.states.set(photoNumber, { ...state, outputFile });
    this.showPreview(photoNumber, blob);
  }

  showPreview(photoNumber, blob) {
    const slot = this.querySelector(`[data-photo-slot="${photoNumber}"]`);
    const oldUrl = this.previewUrls.get(photoNumber);
    if (oldUrl) URL.revokeObjectURL(oldUrl);
    const url = URL.createObjectURL(blob);
    this.previewUrls.set(photoNumber, url);
    const image = slot?.querySelector('[data-preview-image]');
    if (image) image.src = url;
    const picker = slot?.querySelector('[data-picker]');
    const preview = slot?.querySelector('[data-preview]');
    if (picker) picker.hidden = true;
    if (preview) preview.hidden = false;
  }

  removePhoto(photoNumber) {
    const input = this.querySelector(`[data-photo-input="${photoNumber}"]`);
    const slot = this.querySelector(`[data-photo-slot="${photoNumber}"]`);
    if (input) {
      input.value = '';
      input.setCustomValidity('Please add this pendant photo.');
    }
    const oldUrl = this.previewUrls.get(photoNumber);
    if (oldUrl) URL.revokeObjectURL(oldUrl);
    this.previewUrls.delete(photoNumber);
    this.states.delete(photoNumber);
    const image = slot?.querySelector('[data-preview-image]');
    if (image) image.removeAttribute('src');
    const picker = slot?.querySelector('[data-picker]');
    const preview = slot?.querySelector('[data-preview]');
    if (picker) picker.hidden = false;
    if (preview) preview.hidden = true;
    this.setSlotStatus(photoNumber, '');
    this.clearGlobalError();
  }

  openEditor(photoNumber) {
    const state = this.states.get(photoNumber);
    if (!state || !(this.editorPanel instanceof HTMLElement)) return;
    this.activePhoto = photoNumber;
    this.workingState = {
      ...state,
      rotation: state.rotation,
      zoom: state.zoom,
      offsetX: state.offsetX,
      offsetY: state.offsetY,
    };
    if (this.details) this.details.open = true;
    if (this.editorTitle) this.editorTitle.textContent = `Edit photo ${photoNumber}`;
    if (this.zoomInput) this.zoomInput.value = String(this.workingState.zoom);
    this.editorPanel.hidden = false;
    this.renderEditor();
    this.editorPanel.scrollIntoView({ behavior: 'auto', block: 'start' });
  }

  closeEditor() {
    if (this.editorPanel) this.editorPanel.hidden = true;
    this.activePhoto = null;
    this.workingState = null;
    this.dragState = null;
  }

  completeUpload() {
    const inputs = [...this.querySelectorAll('[data-photo-input]')];
    const missing = inputs.filter((input) => !input.files?.length);
    if (missing.length || this.dataset.processing) {
      if (this.details) this.details.open = true;
      this.showGlobalError(this.dataset.processing ? 'Please wait while your photos are prepared.' : 'Please add both photos.');
      if (!this.dataset.processing) missing[0]?.focus();
      this.productForm?.reportValidity();
      return;
    }
    this.closeEditor();
    this.clearGlobalError();
    this.dataset.complete = 'true';
    if (this.details) this.details.open = false;
  }

  rotate(amount) {
    if (!this.workingState) return;
    this.workingState.rotation = (this.workingState.rotation + amount + 360) % 360;
    this.workingState.offsetX = 0;
    this.workingState.offsetY = 0;
    this.renderEditor();
  }

  resetEdit() {
    if (!this.workingState) return;
    this.workingState.rotation = 0;
    this.workingState.zoom = 1;
    this.workingState.offsetX = 0;
    this.workingState.offsetY = 0;
    if (this.zoomInput) this.zoomInput.value = '1';
    this.renderEditor();
  }

  async applyEdit() {
    if (!this.workingState || !this.activePhoto) return;
    const photoNumber = this.activePhoto;
    this.dataset.processing = 'true';
    this.setSlotStatus(photoNumber, 'Applying your crop…');
    try {
      await this.commitPhoto(photoNumber, this.workingState);
      this.setSlotStatus(photoNumber, '');
      this.closeEditor();
    } catch (error) {
      console.error('[sunlit-native-upload]', error);
      this.showGlobalError(this.dataset.browserError || 'This browser could not prepare the edited photo.');
    } finally {
      delete this.dataset.processing;
    }
  }

  startDrag(event) {
    if (!this.workingState || !(this.canvas instanceof HTMLCanvasElement)) return;
    this.canvas.setPointerCapture(event.pointerId);
    this.dragState = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
  }

  drag(event) {
    if (!this.workingState || !this.dragState || this.dragState.pointerId !== event.pointerId) return;
    const rect = this.canvas.getBoundingClientRect();
    this.workingState.offsetX += (event.clientX - this.dragState.x) / rect.width;
    this.workingState.offsetY += (event.clientY - this.dragState.y) / rect.height;
    this.dragState.x = event.clientX;
    this.dragState.y = event.clientY;
    this.renderEditor();
  }

  stopDrag(event) {
    if (this.dragState?.pointerId !== event.pointerId) return;
    if (this.canvas?.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
    this.dragState = null;
  }

  renderEditor() {
    if (!this.workingState || !(this.canvas instanceof HTMLCanvasElement)) return;
    this.canvas.width = EDITOR_WIDTH;
    this.canvas.height = EDITOR_HEIGHT;
    this.drawState(this.canvas, this.workingState);
  }

  drawState(canvas, state) {
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) return;
    const width = canvas.width;
    const height = canvas.height;
    const imageWidth = state.image.width;
    const imageHeight = state.image.height;
    const quarterTurn = state.rotation % 180 !== 0;
    const rotatedWidth = quarterTurn ? imageHeight : imageWidth;
    const rotatedHeight = quarterTurn ? imageWidth : imageHeight;
    const baseScale = Math.max(width / rotatedWidth, height / rotatedHeight);
    const scale = baseScale * state.zoom;
    const drawnWidth = rotatedWidth * scale;
    const drawnHeight = rotatedHeight * scale;
    const maxOffsetX = Math.max(0, (drawnWidth - width) / (2 * width));
    const maxOffsetY = Math.max(0, (drawnHeight - height) / (2 * height));
    state.offsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, state.offsetX));
    state.offsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, state.offsetY));

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.save();
    context.translate(width / 2 + state.offsetX * width, height / 2 + state.offsetY * height);
    context.rotate((state.rotation * Math.PI) / 180);
    context.scale(scale, scale);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(state.image, -imageWidth / 2, -imageHeight / 2);
    context.restore();
  }

  setSlotStatus(photoNumber, message) {
    const status = this.querySelector(`[data-photo-slot="${photoNumber}"] [data-slot-status]`);
    if (status) status.textContent = message;
  }

  showGlobalError(message) {
    const status = this.querySelector('[data-upload-status]');
    if (status) status.textContent = message;
  }

  clearGlobalError() {
    this.showGlobalError('');
  }
}

if (!customElements.get('sunlit-pendant-upload')) {
  customElements.define('sunlit-pendant-upload', SunlitPendantUpload);
}
